import { useEffect, useState, memo } from 'react'
import { Link, useLocation } from 'react-router-dom'
import { House, CalendarBlank, QrCode, ChartBar, User, Scan } from '@phosphor-icons/react'
import { supabase } from '../lib/supabase'
import { useAssignmentStatus } from '../hooks/useAssignmentStatus'
import { canUserScan } from '../utils/permissions'

function MemberBottomNav() {
  const location = useLocation()
  const [userId, setUserId] = useState(null)
  const [canScan, setCanScan] = useState(() => {
    // Initialize from cache immediately
    const cached = localStorage.getItem('userCanScan')
    return cached === 'true'
  })
  const [checkingPermission, setCheckingPermission] = useState(false)

  useEffect(() => {
    const getUserId = async () => {
      const { data: { user } } = await supabase.auth.getUser()
      setUserId(user?.id)
    }
    getUserId()
  }, [])

  const { isAssigned } = useAssignmentStatus(userId)

  // Check scanning permission when userId is available
  useEffect(() => {
    const checkScanPermission = async () => {
      if (!userId) {
        setCanScan(false)
        localStorage.removeItem('userCanScan')
        return
      }

      // Don't show loading if we have cached value
      const cached = localStorage.getItem('userCanScan')
      if (!cached) {
        setCheckingPermission(true)
      }

      const hasPermission = await canUserScan(userId)
      setCanScan(hasPermission)
      localStorage.setItem('userCanScan', hasPermission.toString())
      setCheckingPermission(false)
    }

    checkScanPermission()
  }, [userId])

  const isActive = (path) => location.pathname === path

  return (
    <nav className="fixed bottom-0 left-0 right-0 bg-white border-t shadow-lg safe-area-bottom z-50">
      <div className={`grid ${isAssigned ? 'grid-cols-5' : 'grid-cols-4'} h-16 transition-all duration-300`}>
        <Link
          to="/member/home"
          className={`flex flex-col items-center justify-center transition-colors ${
            isActive('/member/home')
              ? 'text-blue-600 border-t border-blue-600'
              : 'text-gray-600 hover:text-gray-900'
          }`}
        >
          <House size={24} weight={isActive('/member/home') ? 'fill' : 'light'} />
          <span className={`text-xs mt-1 ${isActive('/member/home') ? 'font-bold' : ''}`}>Home</span>
        </Link>

        <Link
          to="/member/events"
          className={`flex flex-col items-center justify-center transition-colors ${
            isActive('/member/events')
              ? 'text-blue-600 border-t border-blue-600'
              : 'text-gray-600 hover:text-gray-900'
          }`}
        >
          <CalendarBlank size={24} weight={isActive('/member/events') ? 'fill' : 'light'} />
          <span className={`text-xs mt-1 ${isActive('/member/events') ? 'font-bold' : ''}`}>Events</span>
        </Link>

        {/* Middle tab - only show if assigned */}
        {isAssigned && (
          canScan ? (
            <Link
              to="/member/scanner"
              className={`flex flex-col items-center justify-center transition-colors ${
                isActive('/member/scanner')
                  ? 'text-blue-600 border-t border-blue-600'
                  : 'text-gray-600 hover:text-gray-900'
              }`}
            >
              <Scan size={24} weight={isActive('/member/scanner') ? 'fill' : 'light'} />
              <span className={`text-xs mt-1 ${isActive('/member/scanner') ? 'font-bold' : ''}`}>Scan</span>
            </Link>
          ) : checkingPermission ? (
            <div className="flex flex-col items-center justify-center text-gray-400">
              <QrCode size={24} weight="light" className="opacity-50" />
              <span className="text-xs mt-1">...</span>
            </div>
          ) : (
            <Link
              to="/member/code"
              className={`flex flex-col items-center justify-center transition-colors ${
                isActive('/member/code')
                  ? 'text-blue-600 border-t border-blue-600'
                  : 'text-gray-600 hover:text-gray-900'
              }`}
            >
              <QrCode size={24} weight={isActive('/member/code') ? 'fill' : 'light'} />
              <span className={`text-xs mt-1 ${isActive('/member/code') ? 'font-bold' : ''}`}>Code</span>
            </Link>
          )
        )}

        <Link
          to="/member/stats"
          className={`flex flex-col items-center justify-center transition-colors ${
            isActive('/member/stats')
              ? 'text-blue-600 border-t border-blue-600'
              : 'text-gray-600 hover:text-gray-900'
          }`}
        >
          <ChartBar size={24} weight={isActive('/member/stats') ? 'fill' : 'light'} />
          <span className={`text-xs mt-1 ${isActive('/member/stats') ? 'font-bold' : ''}`}>Stats</span>
        </Link>

        <Link
          to="/member/profile"
          className={`flex flex-col items-center justify-center transition-colors ${
            isActive('/member/profile')
              ? 'text-blue-600 border-t border-blue-600'
              : 'text-gray-600 hover:text-gray-900'
          }`}
        >
          <User size={24} weight={isActive('/member/profile') ? 'fill' : 'light'} />
          <span className={`text-xs mt-1 ${isActive('/member/profile') ? 'font-bold' : ''}`}>Profile</span>
        </Link>
      </div>
    </nav>
  )
}

export default memo(MemberBottomNav)